"use client"

import type React from "react"

import { useState, useRef, useEffect, forwardRef } from "react"
import { Input } from "../components/ui/input"

interface DateInputWithMaskProps {
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  className?: string
}

// Componente reutilizável para usar no seu FormField
const DateInputWithMask = forwardRef<HTMLInputElement, DateInputWithMaskProps>(
  ({ value = "", onChange, placeholder = "dd/mm/yyyy", className, ...props },) => {
    const [internalValue, setInternalValue] = useState(value)
    const [cursorPosition, setCursorPosition] = useState(0)
    const inputRef = useRef<HTMLInputElement>(null)

    // Sincroniza com o valor externo
    useEffect(() => {
      setInternalValue(value)
    }, [value])

    const formatDate = (input: string) => {
      const numbers = input.replace(/\D/g, "")

      if (numbers.length <= 2) {
        return numbers
      } else if (numbers.length <= 4) {
        return `${numbers.slice(0, 2)}/${numbers.slice(2)}`
      } else {
        return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`
      }
    }

    const calculateCursorPosition = (oldValue: string, newValue: string, oldCursor: number) => {
      // Se o novo valor é maior, provavelmente adicionou uma barra
      if (newValue.length > oldValue.length) {
        return oldCursor + (newValue.length - oldValue.length)
      }
      // Se o novo valor é menor, mantém a posição ou ajusta
      return Math.min(oldCursor, newValue.length)
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value
      const cursorPos = e.target.selectionStart || 0

      const formatted = formatDate(inputValue)
      const newCursorPos = calculateCursorPosition(internalValue, formatted, cursorPos)

      setInternalValue(formatted)
      setCursorPosition(newCursorPos)

      // Chama o onChange externo
      onChange?.(formatted)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      const input = e.currentTarget
      const cursorPos = input.selectionStart || 0

      // Melhora o comportamento do backspace
      if (e.key === "Backspace" && input.value[cursorPos - 1] === "/") {
        e.preventDefault()
        const newPos = cursorPos - 1

        // Remove o caractere antes da barra
        const newValue = input.value.slice(0, newPos - 1) + input.value.slice(newPos)
        const formatted = formatDate(newValue)

        setInternalValue(formatted)
        setCursorPosition(newPos - 1)
        onChange?.(formatted)
      }
    }

    // Restaura a posição do cursor após a formatação
    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(cursorPosition, cursorPosition)
      }
    }, [internalValue, cursorPosition])

    return (
      <Input
        ref={inputRef}
        value={internalValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        maxLength={10}
        className={className}
        {...props}
      />
    )
  },
)

DateInputWithMask.displayName = "DateInputWithMask"

export default DateInputWithMask
